/**
 * ATL-1445 stage 3, P6 — the LOCAL VIEW: what this device shows for its own master writes before the
 * server has them (plan `docs/technical/write-cutover-stage3-app-seam.md` §3, §9 risk 5; as built §15).
 *
 * In Firestore mode the SDK's latency compensation shows a till its own unsent writes: the stock it
 * just sold, today's totals, the shift it just opened, the sale in the list. A backend-first till has
 * no SDK cache, so it needs the same thing built from its outbox v2 entries. This engine keeps that
 * view and publishes it as ONE immutable snapshot (`localViewAtom`); `AtomsProvider` folds it over the
 * server atoms (`serverProductsDictAtom`, `serverDailyStatsAtom`) to derive the read-only
 * `productsDictAtom` and `dailyStatsAtom`, and the operations list / client search merge its rows.
 *
 * DARK. The view is ACTIVE only for a session whose outbox v2 can hold entries (`activateLocalView`,
 * called where P5 starts the session's drainer). No organization is Supabase-master and no bundle is
 * capable, so for every session today the view is never activated, the snapshot stays the frozen
 * `EMPTY_LOCAL_VIEW`, and every derived atom returns the server object itself. Nothing here opens a
 * store, a timer, a BroadcastChannel or asks for persistent storage until activation; the generation
 * calls on the read paths are integer bookkeeping.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * LIFECYCLE OF AN ENTRY'S EFFECTS
 *
 *   queued ──► PENDING ──(server acknowledged)──► ACKED, NOT YET CONFIRMED ──(surface confirms)──► gone
 *                 │
 *                 └──(purged by a delete, discarded by an operator)──► gone at once
 *
 * PENDING: every effect shows. ACKED: the drainer delivered it; it moves to an in-memory set with the
 * generation of each surface at that moment, and keeps showing until the SURFACE confirms it,
 * separately per surface (§3):
 *
 *  - STOCK and the product records, per product K: retired while the value the products surface
 *    LAST WROTE for K came from a read that STARTED AFTER the ack — a full catalogue read covers every
 *    key, a single-product read its own. Reads `begin` when their request is issued and are written
 *    through `commitLocalViewRead` (`subscriptionFactory`, `AppStack`'s initial load). "Last wrote",
 *    not "the newest that completed": an older read whose response lands after a newer one writes
 *    pre-commit stock again, and the overlay comes back with it (reviewer S1). A read that started
 *    BEFORE the ack may have read the server before the write committed, so it retires nothing;
 *    retiring at the ack would make stock jump back until a read lands, keeping it after a post-ack
 *    read would count the sale twice.
 *  - TOTALS and the shift status: the same rule on the daily-stats surface. Its reads start in
 *    `dailyStatsFreshness` (one at a time) and are written in `AppStack.fetchDailyStats` at the
 *    daily-stats write — `/reports/daily-stats` carries the totals AND `shiftOpened`/`shiftClosed`.
 *  - LIST ROWS (sales, clients): retired when a list's server rows contain the id
 *    (`observeLocalViewRows`), or at once when no list of that collection is mounted (one that mounts
 *    later reads after the ack). Wherever the server row is shown the pending row is hidden (the merge
 *    de-duplicates by id).
 *  An acked entry whose every contribution is retired is forgotten — but only once no read of its
 *  surfaces that began before its ack is still unsettled, because that read could still write
 *  pre-commit data and bring the overlay back.
 *
 * THE ACK ASKS FOR THE READ (reviewer B1). Nothing else guarantees that a read starts after the ack:
 * realtime may have fired before the drainer saw the 200, a replayed (lost-ack) write commits nothing
 * new, and the initial load may have started mid-drain. So every acknowledgement — local, from another
 * tab, inferred, or found at a rebuild — requests a read of exactly what it touched: the products the
 * entry names (a full resync above `MAX_KEYED_READS`) and the daily stats (`registerLocalViewReadRequester`,
 * wired by `subscriptionFactory` and `AppStack.subscribeToDailyStats`).
 *
 * The generation counters are integers, never clocks: a read is "after the ack" exactly when its
 * `begin` happened after the ack was recorded, whatever the device clock says.
 *
 * A SESSION THAT READS FIRESTORE (a `refuse` session draining what an earlier session queued, §14 Q2)
 * has no generation source: its products and daily stats come from Firestore listeners that see a
 * master write only once the reverse mirror has carried it back, at a time nothing here can observe.
 * There the stock and totals overlays retire AT THE ACK (a transient under-count until the mirror
 * lands) rather than never (a permanent double count). Open question in the PR.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * RESIDUALS (stated, not fixed)
 *
 *  - LOST ACKNOWLEDGEMENT. An entry sent and committed whose answer never arrived (a dropped
 *    connection after the commit, the app killed mid-send) is still pending here — and on disk, so it
 *    survives a restart — while the server already has it. A read in that window returns stock and
 *    totals that include it AND the overlay still subtracts it: the device shows it TWICE until the
 *    drainer's replay is answered (the backend answers an idempotent replay at once) and the read that
 *    acknowledgement requests lands — seconds, online. The list does not double it (the server row
 *    wins). Nothing short of the server's ack can tell the two cases apart.
 *  - An entry REMOVED from the store without this context hearing of it (another tab's delivery whose
 *    broadcast was lost) is treated as acknowledged at the drainer's next listing taken after it was
 *    tracked (`localViewRelisted`), and the read is requested as for any ack.
 *  - Held entries keep showing (the write is still owed; only a person resolves it, invariant 14).
 *    Entries queued by ANOTHER install (a restored backup, `foreignInstall`) never show: the old install
 *    may already have delivered them, and nothing is sent until an operator adopts them.
 */
import { getDefaultStore } from 'jotai'

import { parseLocalEffects, type LocalEffects, type RecordCollection } from './localEffects'
import { validateStoredEntry, type JsonObject, type OutboxEntryV2 } from './outboxEntry'
import type { OutboxSlot, OutboxStore } from './outboxStore'
import { writeWAckDiscarded, writeWAckFromChannel } from './writeWAcks'
import {
  EMPTY_LOCAL_VIEW,
  localViewAtom,
  type DailyStatOverlayOp,
  type DailyStatsOverlay,
  type LocalViewSnapshot,
  type PendingRow,
  type ProductOverlayOp,
  type ProductsOverlay,
  type RowCollection,
  type RowsOverlay,
} from './localViewState'

// ============================================================================
// Generations
// ============================================================================

export type LocalViewSurface = 'products' | 'dailyStats'

/** A read of one surface, from its start (`begin`) to the moment its result was written (or abandoned). */
export interface RefetchToken {
  readonly surface: LocalViewSurface
  readonly seq: number
}

interface AckRecord {
  readonly at: number
  /** The surfaces' `begin` counters when the ack was recorded: a read with a higher seq started after it. */
  readonly products: number
  readonly dailyStats: number
}

/** What an acknowledged, not yet confirmed entry still needs a read of. */
export interface ReadsToRequest {
  productKeys: string[]
  dailyStats: boolean
}

// ============================================================================
// The engine (pure state; the app binding is below)
// ============================================================================

export type LocalViewReport =
  | { kind: 'unreadable-effects'; orgFirestoreId: string; entryId: string; label: string; reason: string }
  | { kind: 'inferred-ack'; orgFirestoreId: string; entryIds: string[] }

export interface LocalViewEngineOptions {
  publish: (snapshot: LocalViewSnapshot) => void
  now?: () => number
  /** The store's current business day, `YYYY-MM-DD`, or null. */
  businessDayOf?: (storeId: string, now: number) => string | null
  report?: (event: LocalViewReport) => void
}

interface Tracked {
  readonly entryId: string
  readonly seq: number
  /** The engine event at which it was first tracked (orders equal seqs; guards inferred acks). */
  readonly trackedAt: number
  readonly effects: LocalEffects
  ack: AckRecord | null
}

const MAX_REMEMBERED_IDS = 1000

/** A set of ids that forgets its oldest past `MAX_REMEMBERED_IDS` (bounded memory for a long session). */
interface BoundedIds {
  knows(id: string): boolean
  remember(id: string): void
  reset(): void
}

function boundedIds(): BoundedIds {
  const ids = new Set<string>()
  return {
    knows: (id) => ids.has(id),
    remember: (id) => {
      if (ids.has(id)) return
      if (ids.size >= MAX_REMEMBERED_IDS) {
        const oldest = ids.values().next().value
        if (oldest !== undefined) ids.delete(oldest)
      }
      ids.add(id)
    },
    reset: () => ids.clear(),
  }
}

export interface LocalViewEngine {
  activate(orgFirestoreId: string, options: { readsSupabase: boolean }): void
  deactivate(): void
  activeOrganization(): string | null
  /**
   * Install the persisted entries of a listing. Entries already tracked keep their state. When
   * `listedAfterEvent` is given (the `eventCount()` read just BEFORE the listing started), a PENDING
   * entry tracked at or before it and absent from the listing has left the store unseen and is treated
   * as acknowledged now.
   */
  rebuild(orgFirestoreId: string, slots: readonly OutboxSlot[], options?: { listedAfterEvent?: number }): void
  queued(entry: OutboxEntryV2): void
  acknowledged(orgFirestoreId: string, entryId: string, at: number): void
  discarded(orgFirestoreId: string, entryIds: readonly string[]): void
  beginRefetch(surface: LocalViewSurface): RefetchToken
  /**
   * The read `token` began has been WRITTEN. `key`: the products dict key a single-product read wrote;
   * omitted = the whole surface. Settles the token.
   */
  completeRefetch(token: RefetchToken, key?: string): void
  /** The read `token` began ended without writing (failed, superseded, disposed). Settles the token. */
  abandonRefetch(token: RefetchToken): void
  observeRows(collection: RowCollection, ids: ReadonlySet<string>): void
  /** A list of `collection` mounted; returns its unmount. */
  listMounted(collection: RowCollection): () => void
  refreshDay(): void
  snapshot(): LocalViewSnapshot
  eventCount(): number
  /** Whether any tracked entry is acknowledged — i.e. whether a written read could retire anything. */
  hasAcknowledged(): boolean
  /** The reads acknowledged entries still wait for (empty in a Firestore-read session). */
  readsAwaited(): ReadsToRequest
  /** Diagnostics for tests: tracked entry ids and whether each is acked. */
  trackedForTests(): Array<{ entryId: string; acked: boolean }>
}

const isRowCollection = (collection: RecordCollection): collection is RowCollection =>
  collection === 'sales' || collection === 'clients'

type Dirty = { products: boolean; dailyStats: boolean; rows: boolean }
const NONE: Dirty = Object.freeze({ products: false, dailyStats: false, rows: false })
const orDirty = (a: Dirty, b: Dirty): Dirty => ({ products: a.products || b.products, dailyStats: a.dailyStats || b.dailyStats, rows: a.rows || b.rows })

const dirtyFor = (effects: LocalEffects): Dirty => ({
  products: effects.stock.length > 0 || effects.records.some((r) => r.collection === 'products'),
  dailyStats: effects.dailyStats.length > 0,
  rows: effects.records.some((r) => isRowCollection(r.collection)),
})

const productKeysOf = (effects: LocalEffects): string[] => {
  const keys = new Set<string>()
  for (const s of effects.stock) keys.add(s.productId)
  for (const r of effects.records) if (r.collection === 'products') keys.add(r.id)
  return [...keys]
}

export function createLocalViewEngine(options: LocalViewEngineOptions): LocalViewEngine {
  const now = options.now ?? Date.now
  const businessDayOf = options.businessDayOf ?? (() => null)
  const report = options.report ?? (() => undefined)

  // ── generations: live for the JS context, active or not ──────────────────────────────────────────
  // A token begun before activation must still compare correctly with an ack recorded after it.
  let begunProducts = 0
  let begunDailyStats = 0
  /** Reads begun and not yet written or abandoned. An older one may still write pre-commit data. */
  const unsettledProducts = new Set<number>()
  const unsettledDailyStats = new Set<number>()
  /** Write order, so "the read whose value is shown now" is the one written LAST, not the newest. */
  let writeOrder = 0
  let productsFullWrite = { seq: 0, order: 0 }
  const productsKeyWrites = new Map<string, { seq: number; order: number }>()
  let dailyStatsWrittenSeq = 0

  let org: string | null = null
  let readsSupabase = true
  let events = 0
  const tracked = new Map<string, Tracked>()
  /** Acks that arrived before their entry was tracked (a delivery racing the startup listing). */
  const earlyAcks = new Map<string, AckRecord>()
  /** Row ids a list has shown from the server, per collection — only ids of tracked rows are kept. */
  const observedSales = boundedIds()
  const observedClients = boundedIds()
  const wasObserved = (collection: RowCollection, id: string): boolean =>
    collection === 'sales' ? observedSales.knows(id) : observedClients.knows(id)
  let mountedSalesLists = 0
  let mountedClientsLists = 0
  const listsMounted = (collection: RowCollection): number => (collection === 'sales' ? mountedSalesLists : mountedClientsLists)
  const reportedUnreadable = boundedIds()
  /**
   * Entries forgotten (acked and confirmed, purged, discarded). A listing taken before that may still
   * name them; they must never be tracked again as pending (a delivered write counted a second time).
   */
  const forgotten = boundedIds()

  let current: LocalViewSnapshot = EMPTY_LOCAL_VIEW

  // ── retirement ───────────────────────────────────────────────────────────────────────────────────
  const productsShownGeneration = (key: string): number => {
    const keyed = productsKeyWrites.get(key)
    return keyed && keyed.order > productsFullWrite.order ? keyed.seq : productsFullWrite.seq
  }
  const productsRetired = (ack: AckRecord | null, key: string): boolean => {
    if (ack === null) return false
    if (!readsSupabase) return true
    return productsShownGeneration(key) > ack.products
  }
  const dailyStatsRetired = (ack: AckRecord | null): boolean => {
    if (ack === null) return false
    if (!readsSupabase) return true
    return dailyStatsWrittenSeq > ack.dailyStats
  }
  const rowRetired = (ack: AckRecord | null, collection: RowCollection, id: string): boolean =>
    ack !== null && (wasObserved(collection, id) || listsMounted(collection) === 0)

  /** Which surfaces of an entry are retired now — compared before/after a change to find what moved. */
  const retiredSignature = (t: Tracked): string => {
    // Computed for pending entries too (all "not retired"), so an entry acknowledged inside the change
    // compares like for like: the ack alone moves nothing unless it retires something.
    const parts: string[] = []
    for (const key of productKeysOf(t.effects)) parts.push(productsRetired(t.ack, key) ? '1' : '0')
    parts.push(t.effects.dailyStats.length > 0 && dailyStatsRetired(t.ack) ? '1' : '0')
    for (const r of t.effects.records) if (isRowCollection(r.collection)) parts.push(rowRetired(t.ack, r.collection, r.id) ? '1' : '0')
    return parts.join('')
  }

  const fullyRetired = (t: Tracked): boolean => {
    if (t.ack === null) return false
    for (const key of productKeysOf(t.effects)) if (!productsRetired(t.ack, key)) return false
    if (t.effects.dailyStats.length > 0 && !dailyStatsRetired(t.ack)) return false
    for (const r of t.effects.records) if (isRowCollection(r.collection) && !rowRetired(t.ack, r.collection, r.id)) return false
    return true
  }

  /** No read that began at or before the ack can still write over what retired it. */
  const safeToForget = (t: Tracked): boolean => {
    if (t.ack === null) return false
    const ack = t.ack
    const touchesProducts = productKeysOf(t.effects).length > 0
    if (touchesProducts && readsSupabase) for (const seq of unsettledProducts) if (seq <= ack.products) return false
    if (t.effects.dailyStats.length > 0 && readsSupabase) for (const seq of unsettledDailyStats) if (seq <= ack.dailyStats) return false
    return true
  }

  const ordered = (): Tracked[] =>
    [...tracked.values()].sort((a, b) => (a.seq !== b.seq ? a.seq - b.seq : a.trackedAt - b.trackedAt))

  // ── building the snapshot, one surface at a time ─────────────────────────────────────────────
  const buildProducts = (): ProductsOverlay | null => {
    const byKey = new Map<string, ProductOverlayOp[]>()
    const push = (key: string, op: ProductOverlayOp) => {
      const list = byKey.get(key)
      if (list) list.push(op)
      else byKey.set(key, [op])
    }
    for (const t of ordered()) {
      // Records first within one write (a create is what its own stock lands on), then its stock in
      // batch order — the order one writer's batch applies them.
      for (const r of t.effects.records) {
        if (r.collection === 'products' && !productsRetired(t.ack, r.id)) {
          push(r.id, { kind: 'record', op: r.op, fields: r.fields })
        }
      }
      for (const s of t.effects.stock) {
        if (!productsRetired(t.ack, s.productId)) push(s.productId, { kind: 'stock', storeId: s.storeId, delta: s.delta })
      }
    }
    return byKey.size === 0 ? null : byKey
  }

  const buildDailyStats = (): DailyStatsOverlay | null => {
    const byStore = new Map<string, DailyStatOverlayOp[]>()
    for (const t of ordered()) {
      if (dailyStatsRetired(t.ack)) continue
      for (const d of t.effects.dailyStats) {
        const op: DailyStatOverlayOp = { dailyStatId: d.dailyStatId, businessDay: d.businessDay, increments: d.increments, assign: d.assign, remove: d.remove ?? [] }
        const list = byStore.get(d.storeId)
        if (list) list.push(op)
        else byStore.set(d.storeId, [op])
      }
    }
    if (byStore.size === 0) return null
    const at = now()
    const today = new Map<string, string | null>()
    for (const storeId of byStore.keys()) {
      let day: string | null = null
      try {
        day = businessDayOf(storeId, at)
      } catch {
        day = null
      }
      today.set(storeId, day)
    }
    return { byStore, todayByStore: today }
  }

  const buildRows = (): RowsOverlay | null => {
    const rows: Record<RowCollection, PendingRow[]> = { sales: [], clients: [] }
    // Per collection and id: fold create/patch/delete in FIFO order.
    const salesById = new Map<string, PendingRow | null>()
    const clientsById = new Map<string, PendingRow | null>()
    for (const t of ordered()) {
      for (const r of t.effects.records) {
        if (!isRowCollection(r.collection)) continue
        if (rowRetired(t.ack, r.collection, r.id)) continue
        const existing = (r.collection === 'sales' ? salesById : clientsById).get(r.id)
        let next: PendingRow | null | undefined = existing
        if (r.op === 'create') {
          if (existing === undefined || existing === null) next = { id: r.id, store: r.store, date: r.date, fields: r.fields }
        } else if (r.op === 'patch') {
          // Only a row this device created is shown from here; a patch to a server row waits for the
          // list's next read (see the PR's open questions).
          if (existing) next = { id: existing.id, store: existing.store, date: existing.date, fields: { ...existing.fields, ...r.fields } as JsonObject }
        } else if (existing !== undefined) {
          next = null
        }
        if (next === existing) continue
        if (r.collection === 'sales') salesById.set(r.id, next === undefined ? null : next)
        else clientsById.set(r.id, next === undefined ? null : next)
      }
    }
    let any = false
    for (const collection of ['sales', 'clients'] as const) {
      for (const row of (collection === 'sales' ? salesById : clientsById).values()) {
        if (row) {
          rows[collection].push(row)
          any = true
        }
      }
    }
    return any ? rows : null
  }

  /** Rebuild the surfaces marked dirty; publish when the snapshot changes; forget what is done. */
  const update = (dirty: Dirty): void => {
    for (const [id, t] of tracked) {
      if (fullyRetired(t) && safeToForget(t)) {
        tracked.delete(id)
        forgotten.remember(id)
      }
    }
    if (!dirty.products && !dirty.dailyStats && !dirty.rows) return
    const products = dirty.products ? buildProducts() : current.products
    const dailyStats = dirty.dailyStats ? buildDailyStats() : current.dailyStats
    const rows = dirty.rows ? buildRows() : current.rows
    if (products === current.products && dailyStats === current.dailyStats && rows === current.rows) return
    current =
      products === null && dailyStats === null && rows === null
        ? EMPTY_LOCAL_VIEW
        : Object.freeze({ products, dailyStats, rows })
    options.publish(current)
  }

  /** Run `change`; rebuild exactly the surfaces whose retirement moved for some acked entry. */
  const withRetirementChanges = (change: () => void): Dirty => {
    const before = new Map<string, string>()
    for (const t of tracked.values()) before.set(t.entryId, retiredSignature(t))
    change()
    let dirty: Dirty = NONE
    for (const t of tracked.values()) {
      const was = before.get(t.entryId)
      if (was !== undefined && was !== retiredSignature(t)) dirty = orDirty(dirty, dirtyFor(t.effects))
    }
    return dirty
  }

  const track = (orgFirestoreId: string, entry: OutboxEntryV2): Tracked | null => {
    if (tracked.has(entry.entryId) || forgotten.knows(entry.entryId)) return null
    const parsed = parseLocalEffects(entry.effects)
    if (!parsed.ok) {
      if (!reportedUnreadable.knows(entry.entryId)) {
        reportedUnreadable.remember(entry.entryId)
        report({ kind: 'unreadable-effects', orgFirestoreId, entryId: entry.entryId, label: entry.label, reason: parsed.reason })
      }
      return null
    }
    const effects = parsed.effects
    if (effects === null || (effects.stock.length === 0 && effects.dailyStats.length === 0 && effects.records.length === 0)) {
      return null
    }
    const early = earlyAcks.get(entry.entryId) ?? null
    earlyAcks.delete(entry.entryId)
    const t: Tracked = { entryId: entry.entryId, seq: entry.seq, trackedAt: ++events, effects, ack: early }
    tracked.set(entry.entryId, t)
    return t
  }

  const ackRecord = (at: number): AckRecord => ({ at, products: begunProducts, dailyStats: begunDailyStats })

  const isActiveFor = (orgFirestoreId: string): boolean => org !== null && org === orgFirestoreId

  const anyAcked = (): boolean => {
    for (const t of tracked.values()) if (t.ack !== null) return true
    return false
  }

  return {
    activate(orgFirestoreId, activation) {
      if (org === orgFirestoreId) return
      if (org !== null) this.deactivate()
      org = orgFirestoreId
      readsSupabase = activation.readsSupabase
    },

    deactivate() {
      org = null
      tracked.clear()
      earlyAcks.clear()
      forgotten.reset()
      observedSales.reset()
      observedClients.reset()
      if (current !== EMPTY_LOCAL_VIEW) {
        current = EMPTY_LOCAL_VIEW
        options.publish(current)
      }
    },

    activeOrganization: () => org,

    rebuild(orgFirestoreId, slots, rebuildOptions = {}) {
      if (!isActiveFor(orgFirestoreId)) return
      const listed = new Set<string>()
      let dirty: Dirty = NONE
      for (const slot of slots) {
        if (slot.status !== 'ok') {
          // Review 1 nit (b): an entry whose record reads as corrupt is still IN the store — still owed.
          // It must not be inferred acknowledged (and retired) for being unreadable.
          if (slot.entryId !== null) listed.add(slot.entryId)
          continue
        }
        if (slot.entry.orgFirestoreId !== orgFirestoreId) continue
        listed.add(slot.entry.entryId)
        if (slot.foreignInstall) continue
        const t = track(orgFirestoreId, slot.entry)
        if (t) dirty = orDirty(dirty, dirtyFor(t.effects))
      }
      const listedAfter = rebuildOptions.listedAfterEvent
      if (listedAfter !== undefined) {
        const inferred: string[] = []
        dirty = orDirty(
          dirty,
          withRetirementChanges(() => {
            for (const t of tracked.values()) {
              if (t.ack === null && t.trackedAt <= listedAfter && !listed.has(t.entryId)) {
                t.ack = ackRecord(now())
                inferred.push(t.entryId)
              }
            }
          }),
        )
        if (inferred.length > 0) report({ kind: 'inferred-ack', orgFirestoreId, entryIds: inferred })
      }
      events += 1
      update(dirty)
    },

    queued(entry) {
      if (!isActiveFor(entry.orgFirestoreId)) return
      const t = track(entry.orgFirestoreId, entry)
      if (t) update(dirtyFor(t.effects))
    },

    acknowledged(orgFirestoreId, entryId, at) {
      if (!isActiveFor(orgFirestoreId)) return
      events += 1
      const t = tracked.get(entryId)
      if (!t) {
        if (earlyAcks.size >= MAX_REMEMBERED_IDS) {
          const oldest = earlyAcks.keys().next().value
          if (oldest !== undefined) earlyAcks.delete(oldest)
        }
        earlyAcks.set(entryId, ackRecord(at))
        return
      }
      if (t.ack !== null) return
      // What shows changes NOW only where the ack itself retires something: a session without a
      // generation source (Firestore reads) retires stock and totals at the ack, and a row that a list
      // already showed from the server — or that no mounted list shows — retires at once. Otherwise
      // nothing visible changes: no surface is rebuilt, nothing re-renders.
      update(withRetirementChanges(() => (t.ack = ackRecord(at))))
    },

    discarded(orgFirestoreId, entryIds) {
      if (!isActiveFor(orgFirestoreId)) return
      events += 1
      let dirty: Dirty = NONE
      for (const id of entryIds) {
        earlyAcks.delete(id)
        const t = tracked.get(id)
        // Forgotten whether or not it was tracked: a listing taken before the purge may still name it.
        tracked.delete(id)
        forgotten.remember(id)
        if (t) dirty = orDirty(dirty, dirtyFor(t.effects))
      }
      update(dirty)
    },

    beginRefetch(surface) {
      if (surface === 'products') {
        begunProducts += 1
        unsettledProducts.add(begunProducts)
        return { surface, seq: begunProducts }
      }
      begunDailyStats += 1
      unsettledDailyStats.add(begunDailyStats)
      return { surface, seq: begunDailyStats }
    },

    completeRefetch(token, key) {
      const record = () => {
        writeOrder += 1
        if (token.surface === 'dailyStats') {
          unsettledDailyStats.delete(token.seq)
          dailyStatsWrittenSeq = token.seq
        } else {
          unsettledProducts.delete(token.seq)
          if (key === undefined) productsFullWrite = { seq: token.seq, order: writeOrder }
          else productsKeyWrites.set(key, { seq: token.seq, order: writeOrder })
        }
      }
      // Nothing acked ⇒ nothing to retire (or bring back). Today's sessions always take this branch:
      // integer bookkeeping only, no publish.
      if (!anyAcked()) {
        record()
        return
      }
      update(withRetirementChanges(record))
    },

    abandonRefetch(token) {
      const wasUnsettled = token.surface === 'dailyStats' ? unsettledDailyStats.delete(token.seq) : unsettledProducts.delete(token.seq)
      // An abandoned read may have been the last thing keeping a confirmed entry tracked.
      if (wasUnsettled && anyAcked()) update(NONE)
    },

    observeRows(collection, ids) {
      if (org === null || current.rows === null) return
      update(
        withRetirementChanges(() => {
          for (const t of tracked.values()) {
            for (const r of t.effects.records) {
              if (r.collection !== collection || !ids.has(r.id) || wasObserved(collection, r.id)) continue
              if (collection === 'sales') observedSales.remember(r.id)
              else observedClients.remember(r.id)
            }
          }
        }),
      )
    },

    listMounted(collection) {
      if (collection === 'sales') mountedSalesLists += 1
      else mountedClientsLists += 1
      let unmounted = false
      return () => {
        if (unmounted) return
        unmounted = true
        const change = () => {
          if (collection === 'sales') mountedSalesLists -= 1
          else mountedClientsLists -= 1
        }
        if (!anyAcked()) change()
        else update(withRetirementChanges(change))
      }
    },

    refreshDay() {
      const overlay = current.dailyStats
      if (overlay === null) return
      const at = now()
      let changed = false
      for (const [storeId, day] of overlay.todayByStore) {
        let next: string | null = null
        try {
          next = businessDayOf(storeId, at)
        } catch {
          next = null
        }
        if (next !== day) changed = true
      }
      update(changed ? { products: false, dailyStats: true, rows: false } : NONE)
    },

    snapshot: () => current,
    eventCount: () => events,
    hasAcknowledged: anyAcked,
    readsAwaited() {
      const keys = new Set<string>()
      let dailyStats = false
      if (org === null || !readsSupabase) return { productKeys: [], dailyStats: false }
      for (const t of tracked.values()) {
        if (t.ack === null) continue
        for (const key of productKeysOf(t.effects)) if (!productsRetired(t.ack, key)) keys.add(key)
        if (t.effects.dailyStats.length > 0 && !dailyStatsRetired(t.ack)) dailyStats = true
      }
      return { productKeys: [...keys], dailyStats }
    },
    trackedForTests: () => [...tracked.values()].map((t) => ({ entryId: t.entryId, acked: t.ack !== null })),
  }
}

// ============================================================================
// The app binding
// ============================================================================

/** The store's business day as `YYYY-MM-DD` (autoclose-aware, `getStoreBusinessDate`). */
function appBusinessDayOf(storeId: string, at: number): string | null {
  // Lazy: timezoneUtils reads the stores atom, and AtomsProvider (which imports this module's state)
  // must not depend on it at load.
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { getStoreBusinessDate } = require('../../utils/timezoneUtils') as {
    getStoreBusinessDate: (storeId: string | null, date: Date) => Date
  }
  const day = getStoreBusinessDate(storeId, new Date(at))
  if (!(day instanceof Date) || !Number.isFinite(day.getTime())) return null
  return `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`
}

function reportToConsole(event: LocalViewReport): void {
  if (event.kind === 'unreadable-effects') {
    console.error('[localView] an outbox entry carries effects this bundle cannot read; they are not shown', event)
  } else {
    console.warn('[localView] entries left the outbox without an acknowledgement here; treated as acknowledged', event)
  }
}

/**
 * The Jotai store the app renders from. `localViewAtom`'s only writer is the `publish` below (a
 * reviewed `NON_FIRESTORE_UPDATE_RECEIVERS` entry in `scripts/writeBackSites.ts`: nothing here reaches
 * Firestore).
 */
const atoms = getDefaultStore()

const appEngine = createLocalViewEngine({
  publish: (snapshot) => atoms.set(localViewAtom, snapshot),
  businessDayOf: appBusinessDayOf,
  report: reportToConsole,
})

// ── reads requested by acknowledgements (reviewer B1) ──────────────────────────────────────────────

/** Above this many products, one acknowledgement asks for the whole catalogue instead of each product. */
export const MAX_KEYED_READS = 10

/** Products: `keys` to re-read, or `null` for the whole catalogue. Daily stats: no argument. */
export type LocalViewReadRequester = (keys: readonly string[] | null) => void

let productsReadRequester: LocalViewReadRequester | null = null
let dailyStatsReadRequester: LocalViewReadRequester | null = null

/**
 * The surface's way to start a read now (`subscriptionFactory`: debounced per-product refetches or the
 * catalogue resync; `AppStack.subscribeToDailyStats`: the freshness instance). Returns its
 * unregistration, which only removes this very requester (a newer subscription's survives).
 */
export function registerLocalViewReadRequester(surface: LocalViewSurface, requester: LocalViewReadRequester): () => void {
  if (surface === 'products') productsReadRequester = requester
  else dailyStatsReadRequester = requester
  // Review 1 S1: an acknowledgement that arrived before this surface could be asked (the drainer runs
  // from the pin; `setupAllSubscriptions` only after the initial load) must not be dropped — ask now for
  // what is still awaited of THIS surface. Nothing is awaited in a session today (the view is never active).
  requestAwaitedReads(surface)
  return () => {
    if (surface === 'products' && productsReadRequester === requester) productsReadRequester = null
    if (surface === 'dailyStats' && dailyStatsReadRequester === requester) dailyStatsReadRequester = null
  }
}

/** Ask each surface (or only `only`) to read what acknowledged entries still wait for. Never throws. */
function requestAwaitedReads(only?: LocalViewSurface): void {
  const awaited = appEngine.readsAwaited()
  try {
    if (only !== 'dailyStats' && awaited.productKeys.length > 0 && productsReadRequester) {
      productsReadRequester(awaited.productKeys.length > MAX_KEYED_READS ? null : awaited.productKeys)
    }
    if (only !== 'products' && awaited.dailyStats && dailyStatsReadRequester) dailyStatsReadRequester(null)
  } catch (error) {
    console.error('[localView] a read could not be requested; it follows the next realtime event or resync', { error })
  }
}

// ── the startup barrier ─────────────────────────────────────────────────────────────────────────────

/** How long the first catalogue write may wait for the startup rebuild (`localViewStartupBarrier`). */
export const LOCAL_VIEW_REBUILD_TIMEOUT_MS = 3000

let startupBarrier: { org: string; promise: Promise<void>; release: () => void } | null = null

/**
 * A session whose outbox v2 can hold entries (`decideStartupOutboxLook` ≠ `none`) opens the barrier as
 * soon as that is known — before the probe and the pin — so the Supabase initial load holds its first
 * catalogue write until the overlay is rebuilt (`settleLocalViewStartupBarrier`), at most
 * `LOCAL_VIEW_REBUILD_TIMEOUT_MS`. Never called for a session today.
 */
export function openLocalViewStartupBarrier(orgFirestoreId: string, timeoutMs = LOCAL_VIEW_REBUILD_TIMEOUT_MS): void {
  if (startupBarrier?.org === orgFirestoreId) return
  let release = () => undefined as void
  const promise = new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, timeoutMs)
    release = () => {
      clearTimeout(timer)
      resolve()
    }
  })
  const barrier = { org: orgFirestoreId, promise, release }
  startupBarrier = barrier
  void promise.then(() => {
    if (startupBarrier === barrier) startupBarrier = null
  })
}

/** The startup rebuild is done (or there is nothing to rebuild). */
export function settleLocalViewStartupBarrier(orgFirestoreId: string): void {
  if (startupBarrier?.org === orgFirestoreId) startupBarrier.release()
}

/**
 * The rebuild the first catalogue write should wait for, or `null` — synchronously, so a session with
 * no rebuild in progress (every session today) does not even take an extra microtask.
 */
export function localViewStartupBarrier(orgFirestoreId: string): Promise<void> | null {
  return startupBarrier && startupBarrier.org === orgFirestoreId ? startupBarrier.promise : null
}

// ── BroadcastChannel (web): other tabs of this device see this tab's entries ──────────────────────
type LocalViewMessage =
  | { type: 'queued'; entry: unknown }
  | { type: 'acknowledged'; entryId: string; at: number }
  | { type: 'discarded'; entryIds: string[] }

interface BroadcastChannelLike {
  postMessage(message: unknown): void
  close(): void
  onmessage: ((event: { data: unknown }) => void) | null
}
type BroadcastChannelCtor = new (name: string) => BroadcastChannelLike

let channel: { org: string; port: BroadcastChannelLike } | null = null
let persistRequested = false

export const localViewChannelName = (orgFirestoreId: string): string => `atlas-outbox-v2-local-view:${orgFirestoreId}`

function broadcastChannelCtor(): BroadcastChannelCtor | null {
  const ctor = (globalThis as { BroadcastChannel?: unknown }).BroadcastChannel
  return typeof ctor === 'function' ? (ctor as BroadcastChannelCtor) : null
}

function isWeb(): boolean {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { Platform } = require('react-native') as { Platform: { OS: string } }
    return Platform.OS === 'web'
  } catch {
    return false
  }
}

/** A message from another tab is DATA: its shape is checked and an entry is re-validated. */
function onChannelMessage(orgFirestoreId: string, data: unknown): void {
  if (data === null || typeof data !== 'object') return
  const message = data as Partial<LocalViewMessage> & Record<string, unknown>
  if (message.type === 'queued') {
    const validated = validateStoredEntry(message.entry)
    if (validated.ok && validated.value.orgFirestoreId === orgFirestoreId) appEngine.queued(validated.value)
  } else if (message.type === 'acknowledged') {
    if (typeof message.entryId === 'string' && typeof message.at === 'number' && Number.isFinite(message.at)) {
      appEngine.acknowledged(orgFirestoreId, message.entryId, message.at)
      requestAwaitedReads()
      // P9: a write this tab is awaiting was delivered by another tab's drainer.
      writeWAckFromChannel(orgFirestoreId, message.entryId)
    }
  } else if (message.type === 'discarded') {
    if (Array.isArray(message.entryIds) && message.entryIds.every((id) => typeof id === 'string')) {
      appEngine.discarded(orgFirestoreId, message.entryIds as string[])
      // P9: a write this tab is awaiting was removed without delivery by another tab (a purge).
      writeWAckDiscarded(orgFirestoreId, message.entryIds as string[])
    }
  }
}

function post(orgFirestoreId: string, message: LocalViewMessage): void {
  if (!channel || channel.org !== orgFirestoreId) return
  try {
    channel.port.postMessage(message)
  } catch (error) {
    console.warn('[localView] the other tabs could not be told', { orgFirestoreId, type: message.type, error })
  }
}

function openChannel(orgFirestoreId: string): void {
  if (!isWeb()) return
  const Ctor = broadcastChannelCtor()
  if (!Ctor) return
  try {
    const port = new Ctor(localViewChannelName(orgFirestoreId))
    port.onmessage = (event) => onChannelMessage(orgFirestoreId, event.data)
    channel = { org: orgFirestoreId, port }
  } catch (error) {
    console.warn('[localView] BroadcastChannel unavailable; other tabs refresh on their next listing', { error })
  }
}

function closeChannel(): void {
  if (!channel) return
  try {
    channel.port.onmessage = null
    channel.port.close()
  } catch {
    // Closing a channel cannot fail in a way that matters here.
  }
  channel = null
}

/**
 * Plan §2/§9 risk 9: ask the browser not to evict the outbox's IndexedDB under storage pressure.
 * Once per JS context, and only from `activateLocalView` — i.e. only for a session whose v2 can hold
 * entries. A refusal is not an error (the browser decides); it is logged.
 */
function requestPersistentStorage(): void {
  if (persistRequested || !isWeb()) return
  persistRequested = true
  const storage = (globalThis as { navigator?: { storage?: { persist?: () => Promise<boolean> } } }).navigator?.storage
  if (!storage || typeof storage.persist !== 'function') return
  try {
    void storage.persist().then(
      (granted) => console.log(`[localView] persistent storage ${granted ? 'granted' : 'not granted'}`),
      (error: unknown) => console.warn('[localView] persistent storage request failed', { error }),
    )
  } catch (error) {
    console.warn('[localView] persistent storage request failed', { error })
  }
}

// ── the session's view ──────────────────────────────────────────────────────────────────────────────

/**
 * Start the session's local view (P5's `startSessionOutboxAfterPin`, when the session's drainer starts:
 * exactly the sessions whose outbox v2 can hold entries). Rebuilds from `listing` when startup already
 * listed the queue (P5's probe), else lists it here. Either way the startup barrier is settled once the
 * overlay is rebuilt; a listing slower than the barrier's bound is still applied when it arrives
 * (reviewer S2), and the drainer's own listings re-sync the view on every pass (`localViewRelisted`).
 */
export function activateLocalView(
  orgFirestoreId: string,
  options: {
    readsSupabase: boolean
    listing?: readonly OutboxSlot[] | null
    openStore?: () => OutboxStore
  },
): void {
  const wasActive = appEngine.activeOrganization() === orgFirestoreId
  appEngine.activate(orgFirestoreId, { readsSupabase: options.readsSupabase })
  if (!wasActive) {
    closeChannel()
    openChannel(orgFirestoreId)
    requestPersistentStorage()
  }
  if (options.listing) {
    appEngine.rebuild(orgFirestoreId, options.listing)
    requestAwaitedReads()
    settleLocalViewStartupBarrier(orgFirestoreId)
    return
  }
  if (wasActive || !options.openStore) {
    settleLocalViewStartupBarrier(orgFirestoreId)
    return
  }
  const openStore = options.openStore
  const listedAfterEvent = appEngine.eventCount()
  void Promise.resolve()
    .then(() => openStore().list(orgFirestoreId))
    .then(
      (slots) => {
        appEngine.rebuild(orgFirestoreId, slots, { listedAfterEvent })
        requestAwaitedReads()
      },
      (error: unknown) => console.warn('[localView] the outbox could not be listed; the drainer’s listings fill the view', { orgFirestoreId, error }),
    )
    .then(() => settleLocalViewStartupBarrier(orgFirestoreId))
}

/** Stop the local view (sign-out, organization switch). */
export function deactivateLocalView(orgFirestoreId: string): void {
  if (appEngine.activeOrganization() !== orgFirestoreId) return
  appEngine.deactivate()
  closeChannel()
  settleLocalViewStartupBarrier(orgFirestoreId)
}

/** A write was queued (P9's seam, after `enqueuePrepared` resolved). */
export function localViewQueued(entry: OutboxEntryV2): void {
  appEngine.queued(entry)
  post(entry.orgFirestoreId, { type: 'queued', entry })
}

/** The drainer delivered an entry (`OutboxDrainerOptions.onDelivered`); the read it needs is requested. */
export function localViewAcknowledged(orgFirestoreId: string, entryId: string, at: number): void {
  appEngine.acknowledged(orgFirestoreId, entryId, at)
  requestAwaitedReads()
  post(orgFirestoreId, { type: 'acknowledged', entryId, at })
}

/** Entries removed without delivery: purged by a queued delete, discarded by an operator. */
export function localViewDiscarded(orgFirestoreId: string, entryIds: readonly string[]): void {
  if (entryIds.length === 0) return
  appEngine.discarded(orgFirestoreId, entryIds)
  post(orgFirestoreId, { type: 'discarded', entryIds: [...entryIds] })
}

/**
 * The drainer is about to list the queue (`OutboxDrainerOptions.observeListing`). Returns what to call
 * with the listing: entries not yet tracked are installed (a startup listing that failed is recovered
 * here), and entries that left the store unseen since are treated as acknowledged (reviewer S3).
 * `null` when the view is not active for the organization (every session today).
 */
export function localViewListingObserver(orgFirestoreId: string): ((slots: readonly OutboxSlot[]) => void) | null {
  if (appEngine.activeOrganization() !== orgFirestoreId) return null
  const listedAfterEvent = appEngine.eventCount()
  return (slots) => {
    appEngine.rebuild(orgFirestoreId, slots, { listedAfterEvent })
    requestAwaitedReads()
  }
}

// ── reads ──────────────────────────────────────────────────────────────────────────────────────────

/** A read of `surface` is starting. Cheap and always safe to call (a counter). */
export function beginLocalViewRefetch(surface: LocalViewSurface): RefetchToken {
  return appEngine.beginRefetch(surface)
}

/**
 * The read ended WITHOUT writing (failed, superseded, disposed). Idempotent; a no-op after
 * `commitLocalViewRead`. Every `begin` must reach one of the two, or an older read stays "in flight"
 * and confirmed entries are kept tracked (memory, never a wrong value).
 */
export function abandonLocalViewRead(token: RefetchToken): void {
  appEngine.abandonRefetch(token)
}

/** React's batching, where the platform has it (`react-native` and `react-native-web` both export it). */
function batchedUpdates(fn: () => void): void {
  let batch: ((callback: () => void) => void) | undefined
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    batch = (require('react-native') as { unstable_batchedUpdates?: (callback: () => void) => void }).unstable_batchedUpdates
  } catch {
    batch = undefined
  }
  if (typeof batch === 'function') batch(fn)
  else fn()
}

/**
 * Write a read's result (`write`: the server atom's setter calls) AND apply what that read confirms
 * (or, for an older read landing late, un-confirms) as ONE update: no render may show the new server
 * value with the overlay in the old state — not even on a renderer that does not batch updates outside
 * its own event handlers (React Native's legacy renderer re-renders synchronously on each setter call).
 * Inside one `unstable_batchedUpdates`, the server write and the local view's publish reach React
 * together. `key`: the products dict key a single-product read wrote.
 *
 * With nothing acknowledged — every session today — it is `write()` plus integer bookkeeping: no
 * batching, no publish.
 */
export function commitLocalViewRead(token: RefetchToken, write: () => void, key?: string): void {
  commitRead(token, write, key)
}

/**
 * A read that may have been superseded (`AppStack.fetchDailyStats`: a business-day rollover or a
 * read-mode flip started a newer read): when it is no longer current it writes what it writes (its own
 * per-entry guards skip the stale ones) WITHOUT completing its generation — a superseded read confirms
 * nothing (review 1 nit a). With no token it is just the write.
 */
export function commitLocalViewReadIfCurrent(token: RefetchToken | null | undefined, isCurrent: () => boolean, write: () => void): void {
  if (token && isCurrent()) commitRead(token, write)
  else write()
}

function commitRead(token: RefetchToken, write: () => void, key?: string): void {
  if (!appEngine.hasAcknowledged()) {
    write()
    appEngine.completeRefetch(token, key)
    return
  }
  batchedUpdates(() => {
    write()
    appEngine.completeRefetch(token, key)
  })
}

// ── lists ──────────────────────────────────────────────────────────────────────────────────────────

/** A list showed these server rows (`OperationsList`, the client search). */
export function observeLocalViewRows(collection: RowCollection, ids: ReadonlySet<string>): void {
  appEngine.observeRows(collection, ids)
}

/**
 * A list of `collection` mounted (`OperationsList`); returns its unmount. While none is mounted an
 * acknowledged row has nothing to stay visible in, so it retires at once (reviewer S4); a list that
 * mounts later reads after the ack.
 */
export function localViewListMounted(collection: RowCollection): () => void {
  return appEngine.listMounted(collection)
}

/** The business day may have rolled over (`AppStack.subscribeToDailyStats`). No-op without an overlay. */
export function refreshLocalViewDay(): void {
  appEngine.refreshDay()
}

/** Test seam. */
export function localViewEngineForTests(): LocalViewEngine {
  return appEngine
}

/** Test seam: back to a fresh JS context (the generation counters keep running, as in the app). */
export function resetLocalViewForTests(): void {
  const active = appEngine.activeOrganization()
  if (active !== null) deactivateLocalView(active)
  closeChannel()
  if (startupBarrier) startupBarrier.release()
  startupBarrier = null
  persistRequested = false
  productsReadRequester = null
  dailyStatsReadRequester = null
}
